import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Home, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/Shared";
import { api, errMsg, fmtMiles, todayISO } from "@/lib/api";

const empty = () => ({ date: todayISO(), start_odometer: "", end_odometer: "", trip_type: "business", purpose: "", client: "", notes: "" });

const TypeToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-3" data-testid="trip-type-toggle">
    {[
      { v: "business", label: "Business", icon: Briefcase },
      { v: "personal", label: "Personal", icon: Home },
    ].map(({ v, label, icon: Icon }) => (
      <button
        type="button"
        key={v}
        onClick={() => onChange(v)}
        data-testid={`trip-type-${v}`}
        className={`h-14 rounded-xl border-2 flex items-center justify-center gap-2 font-bold transition-colors ${
          value === v
            ? v === "business"
              ? "border-emerald-600 bg-emerald-50 text-emerald-900"
              : "border-slate-700 bg-slate-100 text-slate-900"
            : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
        }`}
      >
        <Icon className="w-5 h-5" />
        {label}
      </button>
    ))}
  </div>
);

export const TripDialog = ({ open, onOpenChange, trip, onSaved }) => {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(trip ? { ...empty(), ...trip } : empty());
  }, [open, trip]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const start = parseFloat(form.start_odometer);
  const end = parseFloat(form.end_odometer);
  const miles = !isNaN(start) && !isNaN(end) ? end - start : null;
  const invalid = miles !== null && miles < 0;

  const submit = async (e) => {
    e.preventDefault();
    if (invalid) return toast.error("Ending odometer must be higher than starting odometer");
    setSaving(true);
    try {
      const payload = { ...form, start_odometer: start, end_odometer: end };
      delete payload.id;
      delete payload.miles;
      delete payload.created_at;
      if (trip) await api.put(`/trips/${trip.id}`, payload);
      else await api.post("/trips", payload);
      toast.success(trip ? "Trip updated" : "Trip saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl p-0 max-h-[92vh] overflow-y-auto" data-testid="trip-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading text-2xl font-bold">{trip ? "Edit Trip" : "Track a Trip"}</DialogTitle>
          <DialogDescription>Enter your odometer readings. We'll calculate the miles.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="px-6 pb-6 space-y-5">
          <Field label="Date" htmlFor="trip-date">
            <Input id="trip-date" type="date" required value={form.date} onChange={set("date")} className="h-12 text-base" data-testid="trip-date-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starting odometer" htmlFor="trip-start">
              <Input id="trip-start" type="number" inputMode="decimal" step="0.1" min="0" required placeholder="e.g. 45210" value={form.start_odometer} onChange={set("start_odometer")} className="h-12 text-base font-mono" data-testid="trip-start-odometer-input" />
            </Field>
            <Field label="Ending odometer" htmlFor="trip-end">
              <Input id="trip-end" type="number" inputMode="decimal" step="0.1" min="0" required placeholder="e.g. 45298" value={form.end_odometer} onChange={set("end_odometer")} className="h-12 text-base font-mono" data-testid="trip-end-odometer-input" />
            </Field>
          </div>
          <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${invalid ? "bg-red-50 text-red-800" : "bg-slate-50 text-slate-700"}`} data-testid="trip-miles-preview">
            <span className="text-sm font-semibold">{invalid ? "Ending must be higher than starting" : "Trip miles"}</span>
            <span className="font-mono text-2xl font-bold">{miles === null || invalid ? "—" : fmtMiles(miles)}</span>
          </div>
          <Field label="Business or personal?">
            <TypeToggle value={form.trip_type} onChange={(v) => setForm((f) => ({ ...f, trip_type: v }))} />
          </Field>
          <Field label="Trip purpose" htmlFor="trip-purpose">
            <Input id="trip-purpose" placeholder="e.g. Delivered load to Dallas" value={form.purpose} onChange={set("purpose")} className="h-12 text-base" data-testid="trip-purpose-input" />
          </Field>
          <Field label="Client / job name" htmlFor="trip-client">
            <Input id="trip-client" placeholder="e.g. Smith Construction" value={form.client} onChange={set("client")} className="h-12 text-base" data-testid="trip-client-input" />
          </Field>
          <Field label="Notes (optional)" htmlFor="trip-notes">
            <Textarea id="trip-notes" rows={2} value={form.notes} onChange={set("notes")} className="text-base" data-testid="trip-notes-input" />
          </Field>
          <button type="submit" disabled={saving} className="mt-btn-primary w-full" data-testid="trip-form-submit">
            {saving && <Loader2 className="w-5 h-5 animate-spin" />}
            {trip ? "Save Changes" : "Save Trip"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
